import FileUploadButtonInput from "@components/FileUploadButtonInput"
import { FileDownload } from "@mui/icons-material"
import { Button, Grid } from "@mui/material"
import { downloadFile, readFile } from "@utils/file"
import { ChangeEvent, HTMLAttributes, useEffect, useState } from "react"
import { useFormContext } from "react-hook-form"
import { InferType } from "yup"
import DeleteIcon from "@mui/icons-material/Delete"
import { useGetGraphValidationFileQuery } from "@store/services/validationService"
import MarkdownPreview from "@components/MarkdownPreview"
import HighlightTextArea, { HighlightArea } from "@components/HighlightTextArea/HighlightTextArea"
import { isEqual, uniqWith } from "lodash"
import { validationSchema } from "@features/builder/ValidationConfiguration/helpers"

export type ValidationConfigurationFormValues = InferType<typeof validationSchema>

export type ValidationConfigurationFormProps = Omit<HTMLAttributes<HTMLFormElement>, "onSubmit"> & {
  onSubmit: (values: ValidationConfigurationFormValues) => void
  onClear: () => void
  graphKey: string
  errorMessages: string[]
}

function getPosition(phrase: string) {
  const regex = /^.*Validation rule (\d+)/
  const match = phrase.match(regex)
  if (match && match.length > 0 && match[1]) {
    const extractedDigit = parseInt(match[1]) // This will contain the first digit found
    return extractedDigit //return to programmer's index
  } else {
    return -1
  }
}

function indexOfOccurrence(haystack: string, needle: string, occurrence: number) {
  let counter = 0
  let index = -1
  do {
    index = haystack.indexOf(needle, index + 1)
  } while (index !== -1 && ++counter < occurrence)
  return index
}

const getHighlightedAreas = (text: string, errors: string[]): HighlightArea[] => {
  const positions = errors.map(getPosition)

  const areasToHighlight = positions.map((x) => ({
    startIndex: indexOfOccurrence(text, "- name:", x),
    endIndex: indexOfOccurrence(text, "- name:", x + 1),
  }))

  return uniqWith(areasToHighlight, isEqual)
}

const ValidationConfigurationForm = ({
  graphKey,
  errorMessages,
  onClear,
  ...props
}: ValidationConfigurationFormProps) => {
  const { data: fetchedValidation } = useGetGraphValidationFileQuery(graphKey)
  const [highlightedAreas, setHighlightedAreas] = useState<HighlightArea[]>([])
  const [markdownContent, setMarkdownContent] = useState("")
  const form = useFormContext<ValidationConfigurationFormValues>()

  useEffect(() => {
    fetch("src/assets/readme/README-validation.md")
      .then((response) => response.text())
      .then((text) => setMarkdownContent(text))
  }, [])

  const text = form.watch("configuration")

  useEffect(() => {
    const areasToHighlight = getHighlightedAreas(text, errorMessages)
    setHighlightedAreas(areasToHighlight)

    // reset form so it is reset dirty form state to show only highlights
    // only if user didn't modified it after receiving errors
    form.reset({ ...form.getValues(), configuration: text })

    // eslint-disable-next-line react-hooks/exhaustive-deps -- we do not want to fire it when "text" changes
  }, [errorMessages])

  const handleImport = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const fileContent = await readFile(file)
    form.setValue("configuration", fileContent, { shouldDirty: true })
  }

  const handleDownload = () => {
    const yamlContent = form.getValues("configuration")
    const blob = new Blob([yamlContent], { type: "text/plain" })
    const blobUrl = URL.createObjectURL(blob)

    downloadFile(`validation-${graphKey}.yaml`, blobUrl)
  }

  return (
    <form
      autoComplete="off"
      style={{ height: "100%" }}
      {...props}
      onSubmit={form.handleSubmit(props.onSubmit)}
    >
      <Grid container direction="column" sx={{ height: "100%" }} gap={1} wrap="nowrap">
        <Grid container gap={1}>
          <Grid item>
            <FileUploadButtonInput label="Import" accept=".yml, .yaml" onChange={handleImport} />
          </Grid>

          <Grid item>
            <Button
              disabled={!form.watch("configuration")}
              startIcon={<FileDownload />}
              variant="contained"
              onClick={handleDownload}
            >
              Download
            </Button>
          </Grid>

          <Grid item>
            <Button
              disabled={!fetchedValidation}
              startIcon={<DeleteIcon />}
              variant="contained"
              onClick={onClear}
            >
              Delete
            </Button>
          </Grid>
        </Grid>
        <Grid container item wrap="nowrap" gap={5} sx={{ overflow: "hidden" }}>
          <Grid item xs>
            <HighlightTextArea
              highlightedAreas={!form.formState.isDirty ? highlightedAreas : []}
              control={form.control}
              name="configuration"
              error={!!form.formState.errors.configuration}
              helperText={form.formState.errors.configuration?.message}
            />
          </Grid>
          <Grid item xs={6} overflow={"scroll"}>
            <MarkdownPreview source={markdownContent} />
          </Grid>
        </Grid>
      </Grid>
    </form>
  )
}

export default ValidationConfigurationForm
